<template>
    <div class="login-page">
        <!-- Background -->
        <div class="bg-layer">
            <div class="bg-gradient"></div>
            <div class="bg-pattern"></div>
            <div class="bg-glow bg-glow-1"></div>
            <div class="bg-glow bg-glow-2"></div>
        </div>

        <!-- Main Container -->
        <div class="main-container">
            <!-- Left Section - Brand Story -->
            <div class="brand-section">
                <div class="brand-content">
                    <!-- Logo -->
                    <div class="logo-wrapper">
                        <div class="logo">
                            <div class="logo-icon">
                                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 22C6 23.1046 6.89543 24 8 24C9.10457 24 10 23.1046 10 22C10 20.8954 9.10457 20 8 20C6.89543 20 6 20.8954 6 22Z" fill="currentColor"/>
                                    <path d="M22 22C22 23.1046 22.8954 24 24 24C25.1046 24 26 23.1046 26 22C26 20.8954 25.1046 20 24 20C22.8954 20 22 20.8954 22 22Z" fill="currentColor"/>
                                    <path d="M4 22V16L7 9H20L25 16H27C28.6569 16 30 17.3431 30 19V22H26M10 22H22M4 22H6M4 16H25M18 16V9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <div class="logo-text">
                                <span class="logo-name"> {{VITE_PROJECT_TYPE == 'dninv' ? 'Drive Nation' : 'reCar'}}</span>
                                <span class="logo-tagline">Inventory Platform</span>
                            </div>
                        </div>
                    </div>

                    <!-- Hero -->
                    <div class="hero">
                        <h1 class="hero-title">
                            Dealership management,<br>
                            <span class="title-highlight">reimagined.</span>
                        </h1>
                        <p class="hero-description">
                            The modern platform for automotive inventory management.
                            Track, analyze, and optimize your entire operation from one powerful dashboard.
                        </p>
                    </div>

                    <!-- Features Slider -->
                    <div class="features-slider">
                        <div class="slider-track">
                            <transition name="slide" mode="out-in">
                                <!-- Real-time Updates -->
                                <div class="feature" v-if="currentFeature === 0" key="0">
                                    <div class="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"/>
                                        </svg>
                                    </div>
                                    <div class="feature-content">
                                        <h3 class="feature-title">Real-time Updates</h3>
                                        <p class="feature-desc">Instant sync across all devices with live inventory tracking and notifications.</p>
                                    </div>
                                </div>
                                <!-- Smart Analytics -->
                                <div class="feature" v-else-if="currentFeature === 1" key="1">
                                    <div class="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/>
                                        </svg>
                                    </div>
                                    <div class="feature-content">
                                        <h3 class="feature-title">Smart Analytics</h3>
                                        <p class="feature-desc">Data-driven insights and comprehensive reports to optimize your operations.</p>
                                    </div>
                                </div>
                                <!-- AWS Cloud Security -->
                                <div class="feature" v-else key="2">
                                    <div class="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/>
                                        </svg>
                                    </div>
                                    <div class="feature-content">
                                        <h3 class="feature-title">AWS Cloud Security</h3>
                                        <p class="feature-desc">Hosted on Amazon Web Services with enterprise-grade infrastructure and protection.</p>
                                    </div>
                                </div>
                            </transition>
                        </div>
                        <div class="slider-controls">
                            <div class="slider-dots">
                                <button
                                    class="slider-dot"
                                    :class="{ 'active': currentFeature === 0 }"
                                    @click="goToFeature(0)"
                                ></button>
                                <button
                                    class="slider-dot"
                                    :class="{ 'active': currentFeature === 1 }"
                                    @click="goToFeature(1)"
                                ></button>
                                <button
                                    class="slider-dot"
                                    :class="{ 'active': currentFeature === 2 }"
                                    @click="goToFeature(2)"
                                ></button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Right Section - Login -->
            <div class="login-section">
                <div class="login-wrapper">
                    <!-- Mobile Logo -->
                    <div class="mobile-logo">
                        <div class="logo-icon">
                            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 22C6 23.1046 6.89543 24 8 24C9.10457 24 10 23.1046 10 22C10 20.8954 9.10457 20 8 20C6.89543 20 6 20.8954 6 22Z" fill="currentColor"/>
                                <path d="M22 22C22 23.1046 22.8954 24 24 24C25.1046 24 26 23.1046 26 22C26 20.8954 25.1046 20 24 20C22.8954 20 22 20.8954 22 22Z" fill="currentColor"/>
                                <path d="M4 22V16L7 9H20L25 16H27C28.6569 16 30 17.3431 30 19V22H26M10 22H22M4 22H6M4 16H25M18 16V9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                        <span class="logo-name">reCar</span>
                    </div>

                    <!-- Login Card -->
                    <div class="login-card">
                        <div class="card-header">
                            <h2 class="card-title">Welcome back</h2>
                            <p class="card-subtitle">Sign in to your account to continue</p>
                        </div>

                        <!-- Form -->
                        <form @submit.prevent="handleLogin" class="login-form">
                            <!-- Error Message -->
                            <Transition name="shake">
                                <div v-if="errors" class="error-message">
                                    <div class="error-icon">
                                        <svg viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                                        </svg>
                                    </div>
                                    <div class="error-content">
                                        <span v-if="errors.length === 1">{{ errors[0] }}</span>
                                        <ul v-else>
                                            <li v-for="error in errors" :key="error">{{ error }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </Transition>

                            <!-- Email Field -->
                            <div class="form-field" :class="{ 'focused': emailFocused, 'has-value': form.email }">
                                <label for="email" class="field-label">Email address</label>
                                <div class="field-input-wrapper">
                                    <input
                                        id="email"
                                        type="email"
                                        v-model="form.email"
                                        @focus="emailFocused = true"
                                        @blur="emailFocused = false"
                                        required
                                        autocomplete="email"
                                        placeholder="Enter your email"
                                    />
                                </div>
                            </div>

                            <!-- Password Field -->
                            <div class="form-field" :class="{ 'focused': passwordFocused, 'has-value': form.password }">
                                <label for="password" class="field-label">Password</label>
                                <div class="field-input-wrapper">
                                    <input
                                        id="password"
                                        :type="showPassword ? 'text' : 'password'"
                                        v-model="form.password"
                                        @focus="passwordFocused = true"
                                        @blur="passwordFocused = false"
                                        required
                                        autocomplete="current-password"
                                        placeholder="Enter your password"
                                    />
                                    <button
                                        type="button"
                                        class="password-toggle"
                                        @click="showPassword = !showPassword"
                                        tabindex="-1"
                                    >
                                        <svg v-if="!showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
                                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                        </svg>
                                        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <path d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <button type="submit" class="submit-button" :disabled="loading">
                                <span class="button-content">
                                    <Transition name="fade" mode="out-in">
                                        <span v-if="loading" key="loading" class="loading-state">
                                            <svg class="spinner" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="32" stroke-linecap="round"/>
                                            </svg>
                                            <span>Signing in...</span>
                                        </span>
                                        <span v-else key="default" class="default-state">
                                            <span>Sign in</span>
                                            <svg viewBox="0 0 20 20" fill="currentColor">
                                                <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd"/>
                                            </svg>
                                        </span>
                                    </Transition>
                                </span>
                                <div class="button-shine"></div>
                            </button>
                        </form>

                        <!-- Footer -->
                        <div class="card-footer">
                            <div class="footer-divider">
                                <span></span>
                            </div>
                            <p class="greeting">{{ greeting }}</p>
                            <p class="powered-by">
                                Powered by <a href="https://carblue.com" target="_blank" rel="noopener">CarBlue</a>
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { getUserPermissionsLogin } from "./../queries/userPermission.graphql";

export default {
    data() {
        return {
            VITE_PROJECT_TYPE: import.meta.env.VITE_PROJECT_TYPE,
            loading: false,
            showPassword: false,
            emailFocused: false,
            passwordFocused: false,
            currentFeature: 0,
            featureInterval: null,
            form: {
                email: '',
                password: '',
                acceptJson: '1',
            },
            errors: null,
            totalFeatures: 3
        }
    },
    computed: {
        greeting() {
            const hour = new Date().getHours();
            const date = new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
            });
            let timeGreeting = 'Good evening';
            if (hour < 12) timeGreeting = 'Good morning';
            else if (hour < 18) timeGreeting = 'Good afternoon';
            return `${timeGreeting} • ${date}`;
        }
    },
    created() {
        localStorage.clear();
    },
    mounted() {
        this.startFeatureSlider();
    },
    beforeUnmount() {
        this.stopFeatureSlider();
    },
    methods: {
        startFeatureSlider() {
            this.featureInterval = setInterval(() => {
                this.currentFeature = (this.currentFeature + 1) % this.totalFeatures;
            }, 4000);
        },
        stopFeatureSlider() {
            if (this.featureInterval) {
                clearInterval(this.featureInterval);
            }
        },
        goToFeature(index) {
            this.currentFeature = index;
            this.stopFeatureSlider();
            this.startFeatureSlider();
        },
        handleLogin() {
            this.loading = true;
            this.errors = null;

            fetch('/refresh-csrf')
                .then(response => response.json())
                .then(data => {
                    const csrfToken = document.querySelector('meta[name="csrf-token"]');
                    if (csrfToken) {
                        csrfToken.setAttribute('content', data.token);
                    }
                    const csrfInputs = document.querySelectorAll('input[name="_token"]');
                    csrfInputs.forEach(input => input.value = data.token);

                    return axios.post('/api/login', this.form);
                })
                .then(res => {
                    if (res.data) {
                        localStorage.setItem('current_user', JSON.stringify(res.data));
                        return this.$apollo.query({
                            query: getUserPermissionsLogin,
                            variables: {
                                user_id: res.data?.id.toString()
                            }
                        });
                    }
                })
                .then(res => {
                    if (res && res.data) {
                        localStorage.setItem('user_permission', res.data.userPermissionsLogin);
                        const currentRole = JSON.parse(localStorage.getItem('current_user')).role.toString();

                        const routes = {
                            '7': '/banker',
                            '8': '/pictures',
                            '3': '/checkin'
                        };

                        window.location.href = routes[currentRole] || '/dashboard';
                    }
                })
                .catch(err => {
                    this.loading = false;
                    console.error('Login Error:', err);

                    if (err.response?.data?.errors) {
                        this.errors = Object.values(err.response.data.errors).flat();
                    } else if (err.message) {
                        this.errors = [err.message];
                    } else {
                        this.errors = ['An unexpected error occurred. Please try again.'];
                    }
                });
        }
    }
};
</script>

<style scoped>
/* ========================================
   CSS Variables & Base Styles
======================================== */
.login-page {
    --primary: #017FBE;
    --primary-light: #0295e0;
    --primary-dark: #016a9e;
    --bg-dark: #09090b;
    --bg-card: #18181b;
    --bg-input: #27272a;
    --border-color: #3f3f46;
    --text-primary: #fafafa;
    --text-secondary: #a1a1aa;
    --text-muted: #71717a;
    --error: #ef4444;
    --success: #22c55e;

    min-height: 100vh;
    background: var(--bg-dark);
    position: relative;
    overflow: hidden;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ========================================
   Background Layers
======================================== */
.bg-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.bg-gradient {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse 100% 80% at 50% -30%, rgba(1, 127, 190, 0.12), transparent 70%),
        radial-gradient(ellipse 80% 60% at 80% 90%, rgba(1, 127, 190, 0.08), transparent 60%),
        radial-gradient(ellipse 60% 50% at 20% 80%, rgba(1, 127, 190, 0.06), transparent 50%);
}

.bg-pattern {
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.bg-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.4;
}

.bg-glow-1 {
    width: 800px;
    height: 800px;
    background: var(--primary);
    top: -400px;
    left: 20%;
    transform: translateX(-50%);
    opacity: 0.12;
}

.bg-glow-2 {
    width: 600px;
    height: 600px;
    background: var(--primary);
    bottom: -300px;
    right: 10%;
    opacity: 0.08;
}

/* ========================================
   Main Layout
======================================== */
.main-container {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1fr;
}

@media (min-width: 1024px) {
    .main-container {
        grid-template-columns: 1fr 1fr;
    }
}

@media (min-width: 1280px) {
    .main-container {
        grid-template-columns: 1.2fr 1fr;
    }
}

/* ========================================
   Brand Section (Left)
======================================== */
.brand-section {
    display: none;
    padding: 3rem;
    position: relative;
}

@media (min-width: 1024px) {
    .brand-section {
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

@media (min-width: 1280px) {
    .brand-section {
        padding: 4rem 5rem;
    }
}

.brand-content {
    max-width: 540px;
    width: 100%;
}

/* Logo */
.logo-wrapper {
    margin-bottom: 3rem;
}

.logo {
    display: inline-flex;
    align-items: center;
    gap: 0.875rem;
}

.logo-icon {
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 4px 20px -4px rgba(1, 127, 190, 0.5);
}

.logo-icon svg {
    width: 24px;
    height: 24px;
}

.logo-text {
    display: flex;
    flex-direction: column;
}

.logo-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.logo-tagline {
    font-size: 0.75rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: 500;
}

/* Hero */
.hero {
    margin-bottom: 2rem;
}

.hero-title {
    font-size: clamp(2.25rem, 4vw, 3.25rem);
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.15;
    letter-spacing: -0.03em;
    margin-bottom: 1.25rem;
}

.title-highlight {
    background: linear-gradient(135deg, var(--primary-light) 0%, #4fc3f7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.hero-description {
    font-size: 1.0625rem;
    color: var(--text-secondary);
    line-height: 1.7;
    max-width: 440px;
}

/* Features Slider */
.features-slider {
    margin-top: 2rem;
}

.slider-track {
    min-height: 100px;
    position: relative;
}

.feature {
    display: flex;
    align-items: flex-start;
    gap: 1.25rem;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 20px;
    backdrop-filter: blur(10px);
}

.feature-icon {
    width: 52px;
    height: 52px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 8px 24px -8px rgba(1, 127, 190, 0.4);
}

.feature-icon svg {
    width: 26px;
    height: 26px;
    color: white;
}

.feature-content {
    flex: 1;
}

.feature-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.375rem;
}

.feature-desc {
    font-size: 0.9375rem;
    color: var(--text-secondary);
    line-height: 1.6;
}

/* Slider Controls */
.slider-controls {
    display: flex;
    justify-content: flex-start;
    margin-top: 1.25rem;
}

.slider-dots {
    display: flex;
    gap: 0.5rem;
}

.slider-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: none;
    background: var(--border-color);
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;
}

.slider-dot:hover {
    background: var(--text-muted);
}

.slider-dot.active {
    width: 32px;
    border-radius: 4px;
    background: var(--primary);
}

/* Slide Transition */
.slide-enter-active,
.slide-leave-active {
    transition: all 0.4s ease;
}

.slide-enter-from {
    opacity: 0;
    transform: translateX(20px);
}

.slide-leave-to {
    opacity: 0;
    transform: translateX(-20px);
}

/* ========================================
   Login Section (Right)
======================================== */
.login-section {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 1.5rem;
    min-height: 100vh;
}

@media (min-width: 640px) {
    .login-section {
        padding: 3rem;
    }
}

@media (min-width: 1024px) {
    .login-section {
        background: transparent;
    }
}

.login-wrapper {
    width: 100%;
    max-width: 400px;
}

/* Mobile Logo */
.mobile-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
}

.mobile-logo .logo-icon {
    width: 40px;
    height: 40px;
}

.mobile-logo .logo-icon svg {
    width: 22px;
    height: 22px;
}

.mobile-logo .logo-name {
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--text-primary);
}

@media (min-width: 1024px) {
    .mobile-logo {
        display: none;
    }
}

/* Login Card */
.login-card {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 20px;
    padding: 2rem;
    box-shadow:
        0 0 0 1px rgba(255, 255, 255, 0.05) inset,
        0 20px 50px -12px rgba(0, 0, 0, 0.5);
}

@media (min-width: 640px) {
    .login-card {
        padding: 2.5rem;
    }
}

.card-header {
    text-align: center;
    margin-bottom: 2rem;
}

.card-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.02em;
    margin-bottom: 0.5rem;
}

.card-subtitle {
    font-size: 0.9375rem;
    color: var(--text-muted);
}

/* Form */
.login-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Error Message */
.error-message {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 12px;
}

.error-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: var(--error);
}

.error-icon svg {
    width: 100%;
    height: 100%;
}

.error-content {
    font-size: 0.875rem;
    color: #fca5a5;
    line-height: 1.5;
}

.error-content ul {
    margin: 0;
    padding-left: 1rem;
}

/* Form Fields */
.form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.field-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    transition: color 0.2s ease;
}

.form-field.focused .field-label {
    color: var(--primary-light);
}

.field-input-wrapper {
    position: relative;
}

.field-input-wrapper input {
    width: 100%;
    padding: 0.875rem 1rem;
    background: var(--bg-input);
    border: 1.5px solid transparent;
    border-radius: 12px;
    font-size: 1rem;
    color: var(--text-primary);
    transition: all 0.2s ease;
    outline: none;
}

.field-input-wrapper input::placeholder {
    color: var(--text-muted);
}

.field-input-wrapper input:hover {
    background: #2d2d30;
}

.field-input-wrapper input:focus {
    background: var(--bg-dark);
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(1, 127, 190, 0.15);
}


/* Password Toggle */
.password-toggle {
    position: absolute;
    right: 0.875rem;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.2s ease;
}

.password-toggle:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-secondary);
}

.password-toggle svg {
    width: 20px;
    height: 20px;
}

/* Submit Button */
.submit-button {
    position: relative;
    width: 100%;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s ease;
    margin-top: 0.5rem;
}

.submit-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow:
        0 10px 40px -10px rgba(1, 127, 190, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.1) inset;
}

.submit-button:active:not(:disabled) {
    transform: translateY(0);
}

.submit-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.button-content {
    position: relative;
    z-index: 1;
}

.loading-state,
.default-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.default-state svg {
    width: 18px;
    height: 18px;
    transition: transform 0.2s ease;
}

.submit-button:hover:not(:disabled) .default-state svg {
    transform: translateX(4px);
}

.spinner {
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.button-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
}

.submit-button:hover:not(:disabled) .button-shine {
    left: 100%;
}

/* Card Footer */
.card-footer {
    margin-top: 2rem;
}

.footer-divider {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
}

.footer-divider span {
    flex: 1;
    height: 1px;
    background: var(--border-color);
}

.greeting {
    text-align: center;
    font-size: 0.8125rem;
    color: var(--text-muted);
    margin-bottom: 0.75rem;
}

.powered-by {
    text-align: center;
    font-size: 0.75rem;
    color: var(--text-muted);
}

.powered-by a {
    color: var(--primary-light);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
}

.powered-by a:hover {
    color: var(--text-primary);
}


/* ========================================
   Transitions
======================================== */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.shake-enter-active {
    animation: shake 0.5s ease;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
}
</style>
